import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Upload, Image as ImageIcon, Loader2, Save, Info } from 'lucide-react';
import HeroBannerUploader from './HeroBannerUploader';
import CoverImageUploader from './CoverImageUploader';
import PublisherSelector from './PublisherSelector';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

// Helper to resolve image URLs (handles both relative and absolute URLs)
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  if (url.startsWith('/')) {
    return `${BACKEND_URL}${url}`;
  }
  return `${BACKEND_URL}/${url}`;
};

const EditSeriesModal = ({ isOpen, onClose, series, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    cover_url: '',
    hero_banner_url: '',
    status: 'ongoing',
    visibility: 'public',
    publisher_id: null,
    publisher_status: null,
  });
  const [publisherLoading, setPublisherLoading] = useState(false);
  const [coverPreview, setCoverPreview] = useState('');
  const [episodesCount, setEpisodesCount] = useState(0);
  const [pagesCount, setPagesCount] = useState(0);
  const [cacheBust, setCacheBust] = useState(Date.now());

  // Reset form when series changes or modal opens
  useEffect(() => {
    if (series && isOpen) {
      const resolvedCoverUrl = resolveImageUrl(series.cover_url);
      setFormData({
        title: series.title || '',
        description: series.description || '',
        cover_url: series.cover_url || '', // Store raw URL for DB
        hero_banner_url: series.hero_banner_url || '',
        status: series.status || 'ongoing',
        visibility: series.visibility || 'public',
        publisher_id: series.publisher_id || null,
        publisher_status: series.publisher_status || null,
      });
      setCoverPreview(resolvedCoverUrl || '');
      setCacheBust(Date.now()); // Reset cache bust on open
      console.log('[EditSeriesModal] Initialized with series:', series.id, 'cover_url:', series.cover_url);
    }
  }, [series, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle publisher selection change
  const handlePublisherChange = async (newPublisherId) => {
    if (newPublisherId === formData.publisher_id) return;
    
    setPublisherLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      if (newPublisherId) {
        // Set new publisher (creates pending request)
        await axios.put(
          `${API}/series/${series.id}/publisher`,
          { publisher_id: newPublisherId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setFormData(prev => ({
          ...prev,
          publisher_id: newPublisherId,
          publisher_status: 'pending'
        }));
        toast.success('Publisher association requested');
      } else {
        // Remove publisher
        await axios.delete(
          `${API}/series/${series.id}/publisher`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setFormData(prev => ({
          ...prev,
          publisher_id: null,
          publisher_status: null
        }));
        toast.success('Publisher association removed');
      }
    } catch (error) {
      console.error('Error updating publisher:', error);
      toast.error(error.response?.data?.detail || 'Failed to update publisher');
    } finally {
      setPublisherLoading(false);
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingCover(true);

    try {
      // Create FormData
      const uploadData = new FormData();
      uploadData.append('file', file);

      console.log('[EditSeriesModal] Uploading cover...');

      // Upload to banner endpoint
      const response = await axios.post(`${API}/upload/banner`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      console.log('[EditSeriesModal] Upload response:', response.data);

      // Handle both absolute URLs (Cloudinary) and relative URLs (local)
      const returnedUrl = response.data.url;
      const newCoverUrl = resolveImageUrl(returnedUrl);
      
      console.log('[EditSeriesModal] New cover URL (resolved):', newCoverUrl);
      console.log('[EditSeriesModal] Raw URL for DB:', returnedUrl);

      // Update state - store raw URL in formData, resolved URL for preview
      handleInputChange('cover_url', returnedUrl); // Store raw URL for DB
      setCoverPreview(newCoverUrl);
      setCacheBust(Date.now()); // Force re-render of image
      
      toast.success('Cover uploaded successfully!');
    } catch (error) {
      console.error('[EditSeriesModal] Error uploading cover:', error);
      toast.error('Failed to upload cover image');
    } finally {
      setUploadingCover(false);
    }
  };

  // Fetch episode/page counts when modal opens
  useEffect(() => {
    const fetchCounts = async () => {
      if (!series?.id) return;
      try {
        const res = await axios.get(`${API}/series/${series.id}/chapters`);
        const chapters = Array.isArray(res.data) ? res.data : [];
        const epCount = chapters.length;
        const pageCount = chapters.reduce((sum, ch) => {
          const pages = Array.isArray(ch.pages_data) ? ch.pages_data.length : 0;
          return sum + pages;
        }, 0);
        setEpisodesCount(epCount);
        setPagesCount(pageCount);
      } catch (error) {
        console.error('Error fetching episode counts:', error);
      }
    };

    if (isOpen) {
      fetchCounts();
    }
  }, [isOpen, series?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    console.log('[EditSeriesModal] Submitting update with cover_url:', formData.cover_url);

    try {
      // Update series
      const response = await axios.put(`${API}/series/${series.id}`, {
        title: formData.title,
        description: formData.description,
        cover_url: formData.cover_url,
        status: formData.status,
        visibility: formData.visibility,
      });

      console.log('[EditSeriesModal] Update response:', response.data);
      console.log('[EditSeriesModal] Response cover_url:', response.data.cover_url);

      toast.success('Series updated successfully!');
      onUpdate(response.data);
      onClose();
    } catch (error) {
      console.error('[EditSeriesModal] Error updating series:', error);
      toast.error(error.response?.data?.detail || 'Failed to update series');
    } finally {
      setLoading(false);
    }
  };

  // Generate preview URL with cache busting
  const getPreviewUrl = () => {
    if (!coverPreview) return '';
    // Add cache bust to force reload after upload
    const separator = coverPreview.includes('?') ? '&' : '?';
    return `${coverPreview}${separator}v=${cacheBust}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-gray-700 max-w-2xl flex flex-col">
        <DialogHeader className="sticky top-0 bg-gray-900 z-10 pb-4 border-b border-gray-800">
          <DialogTitle className="text-2xl font-black text-white">
            Edit Series
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 py-4 flex-1">
          
          {/* Cover Image Upload - Using new component */}
          <div className="space-y-2">
            <CoverImageUploader
              contentType="series"
              contentId={series?.id}
              currentCoverUrl={formData.cover_url}
              onCoverUpdate={(url) => {
                setFormData(prev => ({ ...prev, cover_url: url }));
                setCoverPreview(url ? resolveImageUrl(url) : '');
              }}
              lastUpdated={series?.cover_image_updated_at}
              contentTitle={series?.title}
            />
          </div>

          {/* Hero Banner Upload */}
          <div className="space-y-2">
            <HeroBannerUploader
              contentType="series"
              contentId={series?.id}
              currentBannerUrl={formData.hero_banner_url}
              onBannerUpdate={(url) => {
                setFormData(prev => ({ ...prev, hero_banner_url: url }));
              }}
              lastUpdated={series?.hero_banner_updated_at}
            />
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title" className="text-white font-medium">
              Series Title
            </Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Enter series title"
              className="bg-gray-800 border-gray-600 text-white"
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-white font-medium">

          {/* Visibility + Danger Zone */}
          <div className="space-y-4 border-t border-gray-800 pt-4 mt-4">
            <div className="space-y-2">
              <Label className="text-white font-medium">Series Visibility</Label>
              <p className="text-xs text-gray-400">
                Archive hides this series and its episodes from readers without deleting anything.
              </p>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="border-gray-600 text-gray-200 hover:bg-gray-800 text-xs"
                  onClick={async () => {
                    const newVisibility = formData.visibility === 'public' ? 'archived' : 'public';
                    const actionLabel = newVisibility === 'archived' ? 'Archive' : 'Restore';

                    const confirmed = window.confirm(
                      `${actionLabel} this series? This will ` +
                      (newVisibility === 'archived'
                        ? `hide ${episodesCount} episode(s) and ${pagesCount} page(s) from readers (you can restore later).`
                        : `make this series and its ${episodesCount} episode(s) and ${pagesCount} page(s) visible to readers again.`)
                    );
                    if (!confirmed) return;

                    try {
                      const res = await axios.patch(`${API}/series/${series.id}/visibility`, {
                        visibility: newVisibility,
                      });
                      handleInputChange('visibility', res.data.visibility || newVisibility);
                      toast.success(
                        newVisibility === 'archived'
                          ? 'Series archived successfully'
                          : 'Series restored successfully'
                      );
                      onUpdate(res.data);
                    } catch (error) {
                      console.error('Error updating visibility:', error);
                      toast.error(error.response?.data?.detail || 'Failed to update visibility');
                    }
                  }}
                  disabled={loading || uploadingCover}
                >
                  {formData.visibility === 'public' ? 'Archive Series' : 'Restore Series'}
                </Button>
                <span className="text-xs text-gray-500">
                  {episodesCount} episode(s), {pagesCount} page(s)
                </span>
              </div>
            </div>

            <div className="border border-red-900/60 bg-red-950/30 rounded-lg p-4">
              <p className="text-sm font-semibold text-red-400 mb-2">Danger Zone</p>
              <p className="text-xs text-gray-400 mb-3">
                Deleting a series is permanent and cannot be undone. You must delete all episodes first.
              </p>
              <Button
                type="button"
                variant="outline"
                className="border-red-700 text-red-400 hover:bg-red-900/30 text-xs"
                disabled={loading}
                onClick={async () => {
                  const confirmed = window.prompt(
                    `Type DELETE to permanently delete this series.\n\n` +
                    `This series currently has ${episodesCount} episode(s) and ${pagesCount} page(s).\n` +
                    `You must delete all episodes first.\n\nType DELETE to confirm:`,
                  );
                  if (confirmed !== 'DELETE') return;

                  try {
                    const res = await axios.delete(`${API}/series/${series.id}`);
                    toast.success(res.data?.message || 'Series deleted');
                    onUpdate(null); // signal removal
                    onClose();
                  } catch (error) {
                    console.error('Error deleting series:', error);
                    toast.error(error.response?.data?.detail || 'Failed to delete series');
                  }
                }}
              >
                Delete Series Permanently
              </Button>
            </div>
          </div>

              Description
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Enter series description"
              rows={4}
              className="bg-gray-800 border-gray-600 text-white resize-none"
              required
            />
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label htmlFor="status" className="text-white font-medium">
              Status
            </Label>
            <select
              id="status"
              value={formData.status}
              onChange={(e) => handleInputChange('status', e.target.value)}
              className="w-full bg-gray-800 border border-gray-600 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="ongoing">Ongoing</option>
              <option value="completed">Completed</option>
              <option value="hiatus">On Hiatus</option>
            </select>
          </div>

          {/* Publisher Selector */}
          <PublisherSelector
            value={formData.publisher_id}
            status={formData.publisher_status}
            onChange={handlePublisherChange}
            disabled={publisherLoading}
            className="mt-4"
          />

          {/* Action Buttons - Sticky Footer */}
          <div className="flex space-x-3 pt-4 sticky bottom-0 bg-gray-900 pb-2 border-t border-gray-800 mt-4 -mx-6 px-6">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
              disabled={loading || publisherLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold"
              disabled={loading || uploadingCover || publisherLoading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditSeriesModal;
